/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '@ibm-watson/shared/ts';
import { RootState } from '../../../../ts/state';
import {
  createCatalogItem,
  deleteCatalogItem,
  listCatalogItems,
  loadCatalogItem,
  resetAllCatalogItems,
  resetCurrentCatalogItem
} from './actions';
import { catalogAdapter } from './adapter';

const initialState = catalogAdapter.getInitialState({
  listCatalogItems: {
    status: ApiStatus.IDLE,
    fetched: null,
    error: null
  },
  loadCatalogItem: {
    status: ApiStatus.IDLE,
    error: null
  },
  createCatalogItem: {
    status: ApiStatus.IDLE,
    error: null
  },
  deleteCatalogItem: {
    status: ApiStatus.IDLE,
    error: null
  },

  draftCatalogItem: {
    catalogItem: null,
    specification: {
      valid: null,
      dirty: null
    },
    importErrors: [],
    importErrorSubject: null
  }
});

export const computeState = (updates?: any): any => ({
  ..._.cloneDeep(initialState),
  ...updates
});

const slice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    defineNewCatalogItem(state, action) {
      const { catalogItem } = action.payload;

      state.draftCatalogItem.catalogItem = catalogItem;
      state.draftCatalogItem.specification.valid = true;
      state.draftCatalogItem.specification.dirty = true;
    },
    updateCatalogItemTitle(state, action) {
      const { title } = action.payload;
      state.draftCatalogItem.catalogItem.title = title;
    },
    updateCatalogItemDescription(state, action) {
      const { description } = action.payload;
      state.draftCatalogItem.catalogItem.description = description;
    },
    updateCatalogItemSpecificationType(state, action) {
      const { type } = action.payload;
      if (!_.isNil(state.draftCatalogItem?.catalogItem?.specification)) {
        state.draftCatalogItem.catalogItem.specification.type = type;
      }
    },
    updateCatalogItemSpecificationBody(state, action) {
      const {
        specification: { body }
      } = action.payload;
      state.draftCatalogItem.catalogItem.specification.body = _.merge(
        state.draftCatalogItem?.catalogItem?.specification?.body,
        body
      );
      // If the catalog item already exists, update the spec body
      if (
        !_.isNil(
          catalogAdapter.getSelectors((state: RootState) => state.catalog)
            .selectById
        )
      ) {
        catalogAdapter.updateOne(state, {
          id: state.draftCatalogItem?.catalogItem?.catalog_item_id,
          changes: action.payload
        });
      }
    },
    setImportErrors(state, action) {
      state.draftCatalogItem.importErrors = action.payload.importErrors;
      state.draftCatalogItem.importErrorSubject =
        action.payload.importErrorSubject;
    }
  },
  extraReducers: builder => {
    builder
      // LIST CATALOG ITEMS
      .addCase(listCatalogItems.pending, (state, _action) => {
        state.listCatalogItems.status = ApiStatus.LOADING;
        state.listCatalogItems.error = null;
      })
      .addCase(listCatalogItems.fulfilled, (state, action) => {
        state.listCatalogItems.status = ApiStatus.SUCCEEDED;
        state.listCatalogItems.error = null;
        state.listCatalogItems.fetched = Date.now();

        catalogAdapter.setAll(state, action.payload);
      })
      .addCase(listCatalogItems.rejected, (state, action) => {
        state.listCatalogItems.status = ApiStatus.ERROR;
        state.listCatalogItems.error = action.error;
        state.listCatalogItems.fetched = null;
      })
      // LOAD CATALOG ITEM
      .addCase(loadCatalogItem.pending, (state, _action) => {
        state.loadCatalogItem.status = ApiStatus.LOADING;
        state.loadCatalogItem.error = null;
      })
      .addCase(loadCatalogItem.fulfilled, (state, action) => {
        state.loadCatalogItem.status = ApiStatus.SUCCEEDED;
        state.loadCatalogItem.error = null;

        state.draftCatalogItem.catalogItem = action.payload;

        state.draftCatalogItem.specification.valid = true;
        state.draftCatalogItem.specification.dirty = false;
      })
      .addCase(loadCatalogItem.rejected, (state, action) => {
        state.loadCatalogItem.status = ApiStatus.ERROR;
        state.loadCatalogItem.error = action.error;
      })
      // CREATE CATALOG ITEM
      .addCase(createCatalogItem.pending, (state, _action) => {
        state.createCatalogItem.status = ApiStatus.LOADING;
        state.createCatalogItem.error = null;
      })
      .addCase(createCatalogItem.fulfilled, (state, action) => {
        state.createCatalogItem.status = ApiStatus.SUCCEEDED;
        state.createCatalogItem.error = null;

        catalogAdapter.addOne(state, action.payload);
      })
      .addCase(createCatalogItem.rejected, (state, action) => {
        state.createCatalogItem.status = ApiStatus.ERROR;
        state.createCatalogItem.error = action.error;
      })
      // DELETE CATALOG ITEM
      .addCase(deleteCatalogItem.pending, (state, _action) => {
        state.deleteCatalogItem.status = ApiStatus.LOADING;
        state.deleteCatalogItem.error = null;
      })
      .addCase(deleteCatalogItem.fulfilled, (state, action) => {
        state.deleteCatalogItem.status = ApiStatus.SUCCEEDED;
        state.deleteCatalogItem.error = null;

        catalogAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteCatalogItem.rejected, (state, action) => {
        state.deleteCatalogItem.status = ApiStatus.ERROR;
        state.deleteCatalogItem.error = action.error;
      })
      // RESET
      .addCase(resetCurrentCatalogItem, (state, _action) => {
        state.listCatalogItems.status = ApiStatus.IDLE;
        state.createCatalogItem.status = ApiStatus.IDLE;
        state.draftCatalogItem.catalogItem = null;
      })
      .addCase(resetAllCatalogItems, (state, _action) => {
        state.listCatalogItems.status = ApiStatus.IDLE;
        state.loadCatalogItem.status = ApiStatus.IDLE;
        state.createCatalogItem.status = ApiStatus.IDLE;
        state.deleteCatalogItem.status = ApiStatus.IDLE;

        state.draftCatalogItem.catalogItem = null;
        state.draftCatalogItem.specification.valid = null;
        state.draftCatalogItem.specification.dirty = null;

        // invalid list catalog item caching
        state.listCatalogItems.fetched = null;

        // remove all catalog items from entities
        catalogAdapter.removeAll(state);
      });
  }
});

export default slice.reducer;

export const {
  defineNewCatalogItem,
  updateCatalogItemDescription,
  updateCatalogItemSpecificationBody,
  updateCatalogItemSpecificationType,
  updateCatalogItemTitle,
  setImportErrors
} = slice.actions;
